import React from 'react';
import { Modal, Descriptions, Table, Tag, Divider, Typography, Space, Button } from 'antd';
import { PrinterOutlined, MailOutlined, DownloadOutlined } from '@ant-design/icons';
import { Invoice } from '../../services/paymentService';

const { Title, Text } = Typography;

interface InvoiceDetailModalProps {
  visible: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onSendEmail?: () => void;
  onDownloadPDF?: () => void;
}

const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  visible,
  onClose,
  invoice,
  onSendEmail,
  onDownloadPDF
}) => {
  if (!invoice) return null;

  const lineItemColumns = [
    {
      title: 'Line #',
      dataIndex: 'line_number',
      key: 'line_number',
      width: 70,
    },
    {
      title: 'Description',
      key: 'description',
      render: (record: any) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>
            Concern: {record.concern}
          </div>
          {record.cause && (
            <div style={{ fontSize: 12, color: '#666' }}>
              Cause: {record.cause}
            </div>
          )}
          {record.correction && (
            <div style={{ fontSize: 12, color: '#666' }}>
              Correction: {record.correction}
            </div>
          )}
          {record.is_diagnosis && (
            <Tag color={record.is_diagnosis_waived ? 'green' : 'blue'} style={{ marginTop: 4 }}>
              {record.is_diagnosis_waived ? 'Diagnosis (WAIVED)' : 'Diagnosis'}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Labor',
      key: 'labor',
      width: 120,
      render: (record: any) => (
        <div style={{ textAlign: 'right' }}>
          {record.labor_hours > 0 ? (
            <>
              <div>{record.labor_hours}h @ ${record.labor_rate}/h</div>
              {record.labor_discount_amount > 0 && (
                <div style={{ fontSize: 11, color: '#22c55e' }}>
                  -${record.labor_discount_amount.toFixed(2)} discount
                </div>
              )}
              <div style={{ fontWeight: 500 }}>${record.labor_total?.toFixed(2) || '0.00'}</div>
            </>
          ) : (
            <div style={{ color: '#999' }}>—</div>
          )}
        </div>
      ),
    },
    {
      title: 'Parts',
      key: 'parts',
      width: 100,
      render: (record: any) => (
        <div style={{ textAlign: 'right' }}>
          {record.parts && record.parts.length > 0 ? (
            <div style={{ fontWeight: 500 }}>${record.parts_total?.toFixed(2) || '0.00'}</div>
          ) : (
            <div style={{ color: '#999' }}>—</div>
          )}
        </div>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'line_total',
      key: 'line_total',
      width: 100,
      render: (total: number, record: any) => (
        <div style={{ textAlign: 'right' }}>
          {record.is_diagnosis_waived && record.original_line_total ? (
            <>
              <div style={{ textDecoration: 'line-through', color: '#999', fontSize: 11 }}>
                ${record.original_line_total.toFixed(2)}
              </div>
              <div style={{ fontWeight: 600, color: '#22c55e' }}>
                ${total.toFixed(2)} (FREE!)
              </div>
            </>
          ) : (
            <div style={{ fontWeight: 600 }}>${total.toFixed(2)}</div>
          )}
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>Invoice Details</Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            {invoice.invoice_number}
          </Text>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      centered
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button
          key="email"
          icon={<MailOutlined />}
          onClick={onSendEmail}
          disabled={!onSendEmail}
        >
          Send to Customer
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={onDownloadPDF}
          disabled={!onDownloadPDF}
        >
          Download PDF
        </Button>,
      ]}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Status Badge */}
        <div style={{ textAlign: 'center' }}>
          <Tag color="success" style={{ fontSize: 16, padding: '8px 16px', fontWeight: 600 }}>
            ✓ PAID IN FULL
          </Tag>
        </div>

        {/* Invoice Info */}
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Invoice Number" span={1}>
            <Text strong>{invoice.invoice_number}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Invoice Date" span={1}>
            {new Date(invoice.invoice_date).toLocaleDateString()}
          </Descriptions.Item>
          <Descriptions.Item label="Payment Date" span={1}>
            {invoice.payment_date ? new Date(invoice.payment_date).toLocaleDateString() : 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Payment Method" span={1}>
            {invoice.payment_method === 'card' ? 'Credit/Debit Card' :
             invoice.payment_method === 'cash' ? 'Cash' :
             invoice.payment_method === 'check' ? 'Check' :
             invoice.payment_method === 'affirm' ? 'Affirm' :
             invoice.payment_method || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Status" span={2}>
            <Tag color="green">PAID</Tag>
            {invoice.is_archived && <Tag color="blue">ARCHIVED</Tag>}
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        {/* Line Items */}
        <div>
          <Title level={5}>Services & Parts</Title>
          <Table
            dataSource={invoice.line_items || []}
            columns={lineItemColumns}
            pagination={false}
            rowKey="id"
            size="small"
            expandable={{
              expandedRowRender: (record: any) => {
                if (!record.parts || record.parts.length === 0) return null;
                return (
                  <div style={{ padding: 16, backgroundColor: '#fafafa' }}>
                    <Text strong>Parts Used:</Text>
                    <Table
                      dataSource={record.parts}
                      size="small"
                      pagination={false}
                      rowKey="id"
                      columns={[
                        {
                          title: 'Part Name',
                          dataIndex: 'part_name',
                          key: 'part_name',
                        },
                        {
                          title: 'Part Number',
                          dataIndex: 'part_number',
                          key: 'part_number',
                        },
                        {
                          title: 'Qty',
                          dataIndex: 'quantity',
                          key: 'quantity',
                          width: 60,
                        },
                        {
                          title: 'Unit Price',
                          dataIndex: 'unit_price',
                          key: 'unit_price',
                          width: 100,
                          render: (price: number) => `$${price.toFixed(2)}`,
                        },
                        {
                          title: 'Total',
                          dataIndex: 'total_price',
                          key: 'total_price',
                          width: 100,
                          render: (price: number) => `$${price.toFixed(2)}`,
                        },
                      ]}
                    />
                  </div>
                );
              },
              rowExpandable: (record: any) => record.parts && record.parts.length > 0,
            }}
          />
        </div>

        <Divider />

        {/* Totals */}
        <div style={{ backgroundColor: '#f5f5f5', padding: 16, borderRadius: 8 }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>Subtotal:</Text>
              <Text strong>${invoice.subtotal.toFixed(2)}</Text>
            </div>
            {invoice.tax_amount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Tax ({(invoice.tax_rate * 100).toFixed(2)}%):</Text>
                <Text strong>${invoice.tax_amount.toFixed(2)}</Text>
              </div>
            )}
            <Divider style={{ margin: '8px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text strong style={{ fontSize: 16 }}>Total Amount:</Text>
              <Text strong style={{ fontSize: 20, color: '#22c55e' }}>
                ${invoice.total_amount.toFixed(2)}
              </Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text strong style={{ fontSize: 16 }}>Amount Paid:</Text>
              <Text strong style={{ fontSize: 20, color: '#22c55e' }}>
                ${invoice.amount_paid.toFixed(2)}
              </Text>
            </div>
            <Divider style={{ margin: '8px 0' }} />
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: 12,
              backgroundColor: '#f0fdf4',
              borderRadius: 6,
              border: '2px solid #22c55e'
            }}>
              <Text strong style={{ fontSize: 18, color: '#22c55e' }}>
                ✓ PAID IN FULL - Balance: $0.00
              </Text>
            </div>
          </Space>
        </div>

        {invoice.notes && (
          <div>
            <Text strong>Notes:</Text>
            <div style={{ marginTop: 8, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              {invoice.notes}
            </div>
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default InvoiceDetailModal;
